import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Heading,
  Text,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Box,
} from "@chakra-ui/react";
import axios from "axios";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import Navbar from "../components/Navbar";
import { apiUrl } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import BusinessFormWrapper from "../components/BusinessFormWrapper";
import TermsAlertDialog from "../components/TermsAlertDialog";
import { cleanOptionalUrl } from "../forms/businessFormConfig";

const splitContactName = (name) => {
  const parts = (name || "").trim().split(/\s+/);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const ExternalBusinessForm = () => {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const toast = useToast();
  const { translate } = useLanguage();

  const [isLoading, setIsLoading] = useState(true);
  const [isEligible, setIsEligible] = useState(false);
  const [businessData, setBusinessData] = useState(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const cancelRef = React.useRef();

  // `accountMode` flips to "claim" when the backend reports the email already
  // has an account; the form then shows a single password field for the user
  // to sign in and attach this coupon to their existing account.
  const [accountMode, setAccountMode] = useState("create");

  useEffect(() => {
    const verifySubmission = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/verify-business-submission/${businessId}?token=${token}`
        );

        if (response.data.eligible) {
          setIsEligible(true);
          setBusinessData(response.data.businessData);
        }
      } catch (error) {
        toast({
          title: "Error",
          description:
            error.response?.data?.message || "Invalid or expired link",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setIsEligible(false);
      } finally {
        setIsLoading(false);
      }
    };

    verifySubmission();
  }, [businessId, token, toast]);

  const handleSubmit = async (values, actions, { logo }) => {
    setPendingSubmission({ values, actions, logo });
    setIsAlertOpen(true);
  };

  const proceedWithSubmission = async () => {
    if (!pendingSubmission) return;

    const { values, actions, logo } = pendingSubmission;
    setIsAlertOpen(false);

    try {
      let idTokenForSubmission = null;
      if (accountMode === "claim") {
        try {
          const cred = await signInWithEmailAndPassword(
            auth,
            businessData.email,
            values.password
          );
          idTokenForSubmission = await cred.user.getIdToken();
        } catch (err) {
          toast({
            title: "Sign in failed",
            description:
              "Could not sign in with that password. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          actions.setSubmitting(false);
          setPendingSubmission(null);
          return;
        }
      }

      const formData = new FormData();

      // Add all nested objects as stringified JSON
      formData.append("name", JSON.stringify(values.name));
      formData.append("category", JSON.stringify(values.category));
      formData.append("offerTitle", JSON.stringify(values.offerTitle));
      formData.append(
        "offerDescription",
        JSON.stringify(values.offerDescription)
      );
      formData.append(
        "termsAndExclusions",
        JSON.stringify(values.termsAndExclusions)
      );

      // Add non-nested values
      formData.append("maxValue", values.maxValue || "");
      formData.append("websiteUrl", cleanOptionalUrl(values.websiteUrl));
      formData.append(
        "googleProfileUrl",
        cleanOptionalUrl(values.googleProfileUrl),
      );
      formData.append("email", values.email || "");
      formData.append("phoneNumber", values.phoneNumber || "");
      formData.append("contactName", values.contactName || "");

      // Add businessId and token
      formData.append("businessId", businessId);
      formData.append("token", token);

      // Account fields: idToken for claim mode, password for new-account mode.
      // contactName is the human entered above — derive first/last for the
      // users/{uid} record so the dashboard greets them correctly.
      if (idTokenForSubmission) {
        formData.append("idToken", idTokenForSubmission);
      } else {
        const { firstName, lastName } = splitContactName(values.contactName);
        formData.append("password", values.password);
        formData.append("firstName", firstName);
        formData.append("lastName", lastName);
      }

      // Add logo if exists
      if (logo) {
        formData.append("logo", logo);
      }

      const response = await axios.post(`${apiUrl}/submit-business`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Ensure we're signed in for the dashboard hop. If we just created the
      // account, sign in with the password the user set. If we used idToken
      // (claim mode), they're already signed in from the step above.
      if (accountMode === "create") {
        try {
          await signInWithEmailAndPassword(
            auth,
            businessData.email,
            values.password
          );
        } catch (err) {
          console.warn("Auto sign-in after submit failed:", err);
        }
      }

      toast({
        title: "Success",
        description: "Business information submitted successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      navigate(`/business/${response.data.businessId || businessId}/dashboard`);
    } catch (error) {
      console.error("Submission error:", error);
      const code = error.response?.data?.error;
      if (code === "EMAIL_IN_USE") {
        setAccountMode("claim");
        // Clear the password fields so the user enters their existing password
        actions.setFieldValue("password", "");
        actions.setFieldValue("confirmPassword", "");
        actions.setTouched({});
        toast({
          title: "Account already exists",
          description:
            "We found an existing account for this email. Enter your password in the contact step to claim this coupon.",
          status: "info",
          duration: 7000,
          isClosable: true,
        });
      } else if (code === "EMAIL_MISMATCH") {
        toast({
          title: "Wrong account",
          description:
            "The signed-in account doesn't match the invited email. Sign out and try again.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description:
            error.response?.data?.error ||
            "Failed to submit business information",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
    actions.setSubmitting(false);
    setPendingSubmission(null);
  };

  const onAlertClose = () => {
    setIsAlertOpen(false);
    if (pendingSubmission?.actions) {
      pendingSubmission.actions.setSubmitting(false);
    }
    setPendingSubmission(null);
  };

  if (isLoading) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  if (!isEligible) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <Text mt={4}>
            {translate("businessForm.headers.error.invalidLink")}
          </Text>
        </Alert>
      </Box>
    );
  }

  const renderHeader = () => (
    <Heading size="lg" mb="4">
      {translate("businessForm.headers.external.title")}
    </Heading>
  );

  return (
    <>
      <Navbar external={true} />
      <BusinessFormWrapper
        initialData={businessData}
        businessId={businessId}
        onSubmit={handleSubmit}
        isEditMode={false}
        renderHeader={renderHeader}
        showAccountFields={true}
        accountMode={accountMode}
        onChangeAccountMode={setAccountMode}
      />
      <TermsAlertDialog
        isOpen={isAlertOpen}
        onClose={onAlertClose}
        onProceed={proceedWithSubmission}
        cancelRef={cancelRef}
        useTranslation={true}
      />
    </>
  );
};

export default ExternalBusinessForm;
